import { Chess, type Square } from "chess.js";

export interface BoardPiece {
  type: "p" | "n" | "b" | "r" | "q" | "k";
  color: "w" | "b";
}

export const STARTING_FEN = "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1";

export function squareName(row: number, col: number): Square {
  return `${"abcdefgh"[col]}${8 - row}` as Square;
}

export function coordsFromSquare(sq: Square): { row: number; col: number } {
  const file = sq.charCodeAt(0) - "a".charCodeAt(0);
  const rank = Number(sq[1]);
  return { row: 8 - rank, col: file };
}

/** Return an 8x8 grid of pieces or null from a FEN. White uppercase, black lowercase. */
export function fenToBoard(fen: string): (string | null)[][] {
  const placement = fen.split(" ")[0] ?? "";
  const rows = placement.split("/");
  const board: (string | null)[][] = [];
  for (const row of rows) {
    const r: (string | null)[] = [];
    for (const ch of row) {
      if (/\d/.test(ch)) {
        for (let i = 0; i < Number(ch); i++) r.push(null);
      } else {
        r.push(ch);
      }
    }
    while (r.length < 8) r.push(null);
    board.push(r);
  }
  while (board.length < 8) board.push(Array(8).fill(null));
  return board;
}

export function sideToMove(fen: string): "w" | "b" {
  return (fen.split(" ")[1] as "w" | "b") ?? "w";
}

export function legalDestinationsFrom(fen: string, from: Square): Square[] {
  const c = new Chess(fen);
  const moves = c.moves({ square: from, verbose: true });
  return moves.map((m) => m.to as Square);
}

export interface MoveAttempt {
  from: Square;
  to: Square;
  promotion?: "q" | "r" | "b" | "n";
}

export interface AppliedMove {
  fen: string;
  san: string;
  uci: string;
}

export function tryMove(fen: string, move: MoveAttempt): AppliedMove | null {
  const c = new Chess(fen);
  try {
    const result = c.move({ from: move.from, to: move.to, promotion: move.promotion ?? "q" });
    if (!result) return null;
    return {
      fen: c.fen(),
      san: result.san,
      uci: `${result.from}${result.to}${result.promotion ?? ""}`,
    };
  } catch {
    return null;
  }
}

/** Split a UCI move ("e2e4", "e7e8q") into from/to squares (+ promotion). */
export function uciSquares(uci: string): { from: Square; to: Square; promotion?: string } | null {
  if (!uci || uci.length < 4) return null;
  return {
    from: uci.slice(0, 2) as Square,
    to: uci.slice(2, 4) as Square,
    promotion: uci.length > 4 ? uci.slice(4) : undefined,
  };
}

/** Convert a single UCI move to SAN ("g1f3" -> "Nf3"), given the FEN it's played from.
 *  Falls back to the raw UCI if the move can't be applied to that position. */
export function uciToSan(fen: string | null | undefined, uci: string | null | undefined): string {
  if (!uci) return "";
  if (!fen) return uci;
  const sq = uciSquares(uci);
  if (!sq) return uci;
  try {
    const c = new Chess(fen);
    const m = c.move({ from: sq.from, to: sq.to, promotion: (sq.promotion as "q" | "r" | "b" | "n") ?? "q" });
    return m?.san ?? uci;
  } catch {
    return uci;
  }
}

/** Convert a space-separated UCI line ("e2e4 e7e5 g1f3") to numbered SAN
 *  ("1. e4 e5 2. Nf3"), playing each move forward from ``fen``. */
export function lineToSan(fen: string | null | undefined, line: string | null | undefined): string {
  if (!line) return "";
  if (!fen) return line;
  const c = new Chess(fen);
  const out: string[] = [];
  // Fullmove number is the 6th FEN field.
  let moveNo = Number(fen.split(" ")[5]) || 1;
  let whiteToMove = sideToMove(fen) === "w";
  for (const uci of line.trim().split(/\s+/)) {
    const sq = uciSquares(uci);
    if (!sq) break;
    let san: string;
    try {
      const m = c.move({ from: sq.from, to: sq.to, promotion: (sq.promotion as "q" | "r" | "b" | "n") ?? "q" });
      if (!m) break;
      san = m.san;
    } catch {
      break;
    }
    if (whiteToMove) {
      out.push(`${moveNo}. ${san}`);
    } else {
      out.push(san);
      moveNo += 1;
    }
    whiteToMove = !whiteToMove;
  }
  return out.join(" ");
}

// Per-ply remaining clock (seconds) parsed from PGN ``[%clk H:MM:SS(.f)]``
// comments, in move order. Empty when the PGN carries no clocks (e.g. Lichess
// games fetched without ``clocks=true``).
export function parseClocks(pgn: string | null | undefined): (number | null)[] {
  if (!pgn) return [];
  const out: (number | null)[] = [];
  // Move text starts after the header block (blank line).
  const body = pgn.includes("\n\n") ? pgn.slice(pgn.indexOf("\n\n")) : pgn;
  const re = /\[%clk\s+(\d+):(\d+):(\d+(?:\.\d+)?)\]/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(body)) !== null) {
    out.push(Number(m[1]) * 3600 + Number(m[2]) * 60 + parseFloat(m[3]));
  }
  return out;
}

// Increment (seconds) from a ``[TimeControl "base+inc"]`` or "base" value.
export function parseIncrement(timeControl: string | null | undefined): number {
  if (!timeControl) return 0;
  const m = timeControl.match(/^\d+\+(\d+)/);
  return m ? Number(m[1]) : 0;
}

// Time spent on each ply (seconds): the same player's previous remaining clock
// minus this ply's remaining, plus the increment they banked. Index aligns with
// ``parseClocks`` output. null when unknown.
export function moveDurations(
  clocks: (number | null)[],
  increment: number,
): (number | null)[] {
  return clocks.map((clk, i) => {
    if (clk == null) return null;
    const prev = i >= 2 ? clocks[i - 2] : null; // same player's previous clock
    if (prev == null) return null; // first move for that side — no baseline
    const spent = prev - clk + increment;
    return spent >= 0 ? spent : null;
  });
}

export function formatClock(sec: number | null): string {
  if (sec == null) return "—";
  const s = Math.max(0, Math.round(sec));
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, "0")}`;
}

export function formatSpent(sec: number | null): string {
  if (sec == null) return "";
  const s = Math.max(0, sec);
  if (s < 10) return `${s.toFixed(1)}s`;
  return `${Math.round(s)}s`;
}

export interface PgnPly {
  ply: number;
  fenBefore: string;
  fenAfter: string;
  san: string;
  uci: string;
}

export function replayPgn(pgn: string): PgnPly[] {
  const c = new Chess();
  try {
    c.loadPgn(pgn, { strict: false });
  } catch {
    return [];
  }
  const history = c.history({ verbose: true });
  const out: PgnPly[] = [];
  const replay = new Chess();
  history.forEach((h, i) => {
    const fenBefore = replay.fen();
    replay.move({ from: h.from, to: h.to, promotion: h.promotion });
    out.push({
      ply: i + 1,
      fenBefore,
      fenAfter: replay.fen(),
      san: h.san,
      uci: `${h.from}${h.to}${h.promotion ?? ""}`,
    });
  });
  return out;
}
